/**
 * SimpleConnectionsHub — Panel unificado de conexiones
 * Funciona HOY: Publer (Instagram+TikTok+Facebook+LinkedIn) + WhatsApp Cloud API
 * Modo agencia: un admin conecta todo, los clientes ven el estado.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, XCircle, Zap, RefreshCw, ExternalLink,
  MessageCircle, Mail, Instagram, Send, Globe, Copy, Eye, EyeOff,
  Video, Calendar, Loader2, Play
} from 'lucide-react';
import { toast } from '../ui/sonner';
import PublerClientWizard from './PublerClientWizard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── helpers ─────────────────────────────────────────────────────────────── */
const StatusBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${
    ok ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
       : 'bg-slate-500/15 text-slate-400 border border-slate-500/30'
  }`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {label}
  </span>
);

/* ── Tarjeta de canal ─────────────────────────────────────────────────────── */
function ChannelCard({ icon, name, subtitle, networks, connected, action, note, testId }) {
  return (
    <div className="glass-card p-6 flex flex-col gap-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl ${connected ? 'bg-emerald-500/15' : 'bg-white/5'}`}>
            {icon}
          </div>
          <div>
            <h3 className="text-base font-heading font-semibold text-text-heading">{name}</h3>
            <p className="text-xs text-text-muted mt-0.5">{subtitle}</p>
          </div>
        </div>
        <StatusBadge ok={connected} label={connected ? 'Activo' : 'Sin conectar'} />
      </div>

      {/* Networks */}
      {networks && networks.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {networks.map(n => (
            <span key={n.name} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
              n.ok ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                   : 'border-border-subtle text-text-muted'
            }`}>
              <span>{n.flag}</span> {n.name}
              {n.ok && <CheckCircle2 className="w-2.5 h-2.5" />}
            </span>
          ))}
        </div>
      )}

      {note && (
        <p className="text-xs text-text-muted bg-white/3 px-3 py-2 rounded-xl border border-border-subtle">{note}</p>
      )}

      {action}
    </div>
  );
}

/* ── Publer card ─────────────────────────────────────────────────────────── */
function PublerCard({ authFetch }) {
  const [status, setStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/admin/publer/status`);
      const j = await r.json();
      setStatus(j);
    } catch (e) { /* noop */ }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!apiKey.trim()) { toast.error('Ingresa tu API Key de Publer'); return; }
    setSaving(true);
    try {
      const r = await authFetch(`${API}/admin/publer/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error al conectar Publer');
      toast.success('Publer conectado correctamente');
      setShowForm(false);
      setApiKey('');
      load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const connected = status?.connected;

  return (
    <ChannelCard
      icon="📱"
      name="Publer · Redes Sociales"
      subtitle="Instagram · TikTok · Facebook · LinkedIn — publicación automática sin restricciones"
      testId="publer-channel-card"
      connected={connected}
      networks={[
        { name: 'Instagram', flag: '📸', ok: connected },
        { name: 'TikTok', flag: '🎵', ok: connected },
        { name: 'Facebook', flag: '📘', ok: connected },
        { name: 'LinkedIn', flag: '💼', ok: connected },
        { name: 'YouTube', flag: '▶️', ok: connected },
      ]}
      note={connected
        ? `Cuenta: ${status?.user?.name || 'Publer'} · Aynara puede programar y publicar en todas las redes.`
        : 'Conecta Publer una sola vez y Aynara podrá publicar en todas las redes sin esperar aprobaciones de Meta ni TikTok.'}
      action={
        <div className="space-y-2">
          {!showForm ? (
            <div className="flex flex-wrap gap-2">
              {!connected ? (
                <button
                  onClick={() => setShowForm(true)}
                  className="btn-primary text-sm py-2 px-5 flex items-center gap-2"
                  data-testid="publer-connect-btn"
                >
                  <Zap className="w-4 h-4" /> Conectar Publer
                </button>
              ) : (
                <button onClick={() => setShowForm(true)} className="btn-secondary text-sm py-2 px-4">
                  Cambiar API Key
                </button>
              )}
              <a
                href="https://publer.com/settings/api"
                target="_blank" rel="noreferrer"
                className="btn-secondary text-sm py-2 px-4 flex items-center gap-2"
                data-testid="publer-getkey-link"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Obtener API Key
              </a>
              {connected && (
                <button onClick={load} className="btn-secondary text-sm py-2 px-3">
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-text-muted">
                Ve a <strong>publer.com → Settings → API</strong> y copia tu API Key.
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder="Pega tu API Key de Publer aquí"
                    className="input-base w-full pr-10"
                    data-testid="publer-apikey-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-heading"
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button onClick={save} disabled={saving} className="btn-primary text-sm py-2 px-5" data-testid="publer-save-btn">
                  {saving ? 'Conectando...' : 'Guardar'}
                </button>
                <button onClick={() => { setShowForm(false); setApiKey(''); }} className="btn-secondary text-sm py-2 px-3">
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
}

/* ── WhatsApp card ───────────────────────────────────────────────────────── */
function WhatsAppCard({ authFetch }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    authFetch(`${API}/admin/meta-wa/status`)
      .then(r => r.json())
      .then(j => setStatus(j))
      .catch(() => {});
  }, [authFetch]);

  const connected = status?.configured;
  const phoneId = status?.phone_number_id;

  return (
    <ChannelCard
      icon="💬"
      name="WhatsApp Business"
      subtitle="Mensajes directos, campañas CRM, recordatorios, seguimiento automático"
      testId="whatsapp-channel-card"
      connected={connected}
      note={connected
        ? `Número activo: ${phoneId || '+1 555-142-7289'} · Campañas CRM y mensajes automáticos operativos.`
        : 'Configura tu número de WhatsApp Business para enviar mensajes automáticos desde el CRM.'}
      action={
        connected ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Sistema activo y listo para campañas
            </span>
          </div>
        ) : (
          <a href="/admin/redes" className="btn-primary text-sm py-2 px-5 inline-flex items-center gap-2">
            Configurar WhatsApp
          </a>
        )
      }
    />
  );
}

/* ── Email card ─────────────────────────────────────────────────────────── */
function EmailCard() {
  return (
    <ChannelCard
      icon="✉️"
      name="Email · Resend"
      subtitle="Correos transaccionales, campañas de email, reportes automáticos"
      testId="email-channel-card"
      connected={true}
      note="Canal de email operativo con Resend. Dominio verificado. Aynara envía correos automáticos a tus clientes y leads."
      action={null}
    />
  );
}

/* ── Agency access guide ─────────────────────────────────────────────────── */
function AgencyAccessGuide() {
  const [open, setOpen] = useState(null);

  const steps = {
    meta: [
      { n: 1, text: 'Ve a business.facebook.com e inicia sesión con tu cuenta Facebook.' },
      { n: 2, text: 'En el panel, ve a "Configuración del negocio" (icono de engranaje arriba a la izquierda).' },
      { n: 3, text: 'Selecciona "Usuarios" → "Partners".' },
      { n: 4, text: 'Haz clic en "Agregar" → "Dar acceso a un partner".' },
      { n: 5, text: 'Ingresa el ID de negocio de Neuralia369: 123456789 (te lo confirmamos por WhatsApp).' },
      { n: 6, text: 'Activa el acceso a: Páginas de Facebook, Cuentas de Instagram, Cuentas Publicitarias.' },
      { n: 7, text: 'Haz clic en "Guardar cambios". ¡Listo! En 5 minutos tenemos acceso.' },
    ],
    tiktok: [
      { n: 1, text: 'Ve a business.tiktok.com e inicia sesión.' },
      { n: 2, text: 'Haz clic en "Configuración de la cuenta" (icono de perfil arriba a la derecha).' },
      { n: 3, text: 'Selecciona "BC Center" → "Activos" → "Usuarios".' },
      { n: 4, text: 'Haz clic en "+ Invitar usuario".' },
      { n: 5, text: 'Ingresa el correo de Neuralia369: admin@neuralia369.com.' },
      { n: 6, text: 'Asigna el rol "Operador" para gestión de anuncios y publicaciones.' },
      { n: 7, text: 'Haz clic en "Enviar invitación". Neuralia369 acepta y en 2 minutos está listo.' },
    ],
  };

  return (
    <div className="glass-card p-6 space-y-4" data-testid="agency-access-guide">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
          <Globe className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h3 className="text-base font-heading font-semibold text-text-heading">Acceso directo de agencia</h3>
          <p className="text-xs text-text-muted mt-0.5">Alternativa a OAuth — funciona sin esperar aprobaciones de Meta/TikTok</p>
        </div>
      </div>

      <p className="text-sm text-text-body">
        Si quieres que Aynara publique <strong>directamente</strong> desde las cuentas de tus clientes (sin pasar por Publer), 
        el cliente puede darte acceso de agencia en 5 minutos. Esta es la misma forma en que trabajan las grandes agencias de marketing.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        {[
          { id: 'meta', icon: '📘', name: 'Meta Business Manager', time: '5 min', color: 'border-blue-500/30 bg-blue-500/5' },
          { id: 'tiktok', icon: '🎵', name: 'TikTok Business Center', time: '5 min', color: 'border-pink-500/30 bg-pink-500/5' },
        ].map(p => (
          <div key={p.id}>
            <button
              onClick={() => setOpen(open === p.id ? null : p.id)}
              className={`w-full text-left p-4 rounded-2xl border transition-all ${p.color} hover:border-primary/40`}
              data-testid={`agency-guide-${p.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{p.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-text-heading">{p.name}</p>
                    <p className="text-xs text-text-muted">{p.time} · Sin aprobaciones</p>
                  </div>
                </div>
                <span className="text-xs text-primary">{open === p.id ? '▲ Cerrar' : '▼ Ver pasos'}</span>
              </div>
            </button>

            {open === p.id && (
              <div className="mt-2 space-y-2 pl-2">
                {steps[p.id].map(s => (
                  <div key={s.n} className="flex gap-3 text-sm">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">{s.n}</span>
                    <p className="text-text-body pt-0.5">{s.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── PublerPublishPanel — Publicar videos/imágenes en redes ─────────────── */
function PublerPublishPanel({ authFetch }) {
  const [form, setForm] = useState({
    caption: '',
    video_url: '',
    image_url: '',
    platforms: ['instagram', 'tiktok'],
    scheduled_at: '',
  });
  const [posts, setPosts] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const PLATFORM_OPTIONS = [
    { id: 'instagram', flag: '📸', label: 'Instagram' },
    { id: 'tiktok', flag: '🎵', label: 'TikTok' },
    { id: 'facebook', flag: '📘', label: 'Facebook' },
    { id: 'linkedin', flag: '💼', label: 'LinkedIn' },
    { id: 'youtube', flag: '▶️', label: 'YouTube' },
  ];

  const loadPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const r = await authFetch(`${API}/admin/publer/posts`);
      const j = await r.json();
      setPosts(j.posts || []);
    } catch (e) { /* noop */ }
    setLoadingPosts(false);
  }, [authFetch]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const togglePlatform = (pid) => {
    setForm(p => ({
      ...p,
      platforms: p.platforms.includes(pid)
        ? p.platforms.filter(x => x !== pid)
        : [...p.platforms, pid],
    }));
  };

  const publish = async () => {
    if (!form.caption.trim()) { toast.error('Escribe un caption para el post'); return; }
    if (!form.video_url.trim() && !form.image_url.trim()) { toast.error('Añade una URL de video o imagen'); return; }
    if (form.platforms.length === 0) { toast.error('Selecciona al menos una red social'); return; }
    setPublishing(true);
    try {
      const r = await authFetch(`${API}/admin/publer/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caption: form.caption,
          video_url: form.video_url || undefined,
          image_url: !form.video_url ? form.image_url : undefined,
          platforms: form.platforms,
          scheduled_at: form.scheduled_at || undefined,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error al publicar');
      toast.success(`Post enviado a Publer · ID: ${j.publer_id || '—'}`);
      setForm({ caption: '', video_url: '', image_url: '', platforms: ['instagram', 'tiktok'], scheduled_at: '' });
      loadPosts();
    } catch (e) { toast.error(e.message); }
    setPublishing(false);
  };

  const STATUS_STYLES = {
    scheduled:  'bg-blue-500/15 text-blue-400',
    published:  'bg-emerald-500/15 text-emerald-400',
    failed:     'bg-red-500/15 text-red-400',
  };

  return (
    <div className="space-y-6" data-testid="publer-publish-panel">
      {/* Publicar nuevo post */}
      <div className="glass-card p-6 space-y-5">
        <h3 className="text-base font-heading font-semibold text-text-heading flex items-center gap-2">
          <Video className="w-5 h-5 text-primary" /> Publicar en redes sociales
        </h3>

        {/* Plataformas */}
        <div>
          <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Redes destino</p>
          <div className="flex flex-wrap gap-2">
            {PLATFORM_OPTIONS.map(p => (
              <button
                key={p.id}
                onClick={() => togglePlatform(p.id)}
                data-testid={`publish-platform-${p.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  form.platforms.includes(p.id)
                    ? 'bg-primary/15 border-primary text-primary'
                    : 'border-border-subtle text-text-muted hover:border-primary/30'
                }`}
              >
                <span>{p.flag}</span> {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* URL del media */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-text-muted mb-1 block">URL del video (mp4, mov…)</label>
            <input
              type="url"
              placeholder="https://tu-servidor.com/video.mp4"
              value={form.video_url}
              onChange={e => setForm(p => ({ ...p, video_url: e.target.value }))}
              className="input-base w-full text-sm"
              data-testid="publish-video-url"
            />
            <p className="text-[10px] text-text-muted mt-1">Pega la URL pública del video subido al admin</p>
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1 block">URL de imagen (si no hay video)</label>
            <input
              type="url"
              placeholder="https://tu-servidor.com/imagen.jpg"
              value={form.image_url}
              onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
              className="input-base w-full text-sm"
              data-testid="publish-image-url"
            />
          </div>
        </div>

        {/* Caption */}
        <div>
          <label className="text-xs text-text-muted mb-1 block">Caption / Texto del post</label>
          <textarea
            rows={4}
            placeholder="Escribe tu caption aquí. Puedes incluir hashtags y emojis..."
            value={form.caption}
            onChange={e => setForm(p => ({ ...p, caption: e.target.value }))}
            className="input-base w-full text-sm resize-y"
            data-testid="publish-caption"
          />
        </div>

        {/* Programar (opcional) */}
        <div>
          <label className="text-xs text-text-muted mb-1 block flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> Programar (opcional — deja vacío para publicar ahora)
          </label>
          <input
            type="datetime-local"
            value={form.scheduled_at}
            onChange={e => setForm(p => ({ ...p, scheduled_at: e.target.value }))}
            className="input-base text-sm"
            data-testid="publish-schedule"
          />
        </div>

        <button
          onClick={publish}
          disabled={publishing}
          className="btn-primary py-3 px-8 flex items-center gap-2 text-sm"
          data-testid="publish-submit-btn"
        >
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {publishing ? 'Enviando a Publer...' : `Publicar en ${form.platforms.length} redes`}
        </button>
      </div>

      {/* Historial de posts */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-text-heading">Publicaciones recientes via Publer</h3>
          <button onClick={loadPosts} disabled={loadingPosts} className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loadingPosts ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {posts.length === 0 ? (
          <div className="glass-card p-8 text-center text-text-muted text-sm">
            <Video className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>Aún no hay publicaciones. Usa el formulario de arriba para publicar tu primer post.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(p => (
              <div key={p.id} className="glass-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`publer-post-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-body line-clamp-2">{p.caption || '(sin caption)'}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {(p.platforms || []).map(pl => {
                      const po = PLATFORM_OPTIONS.find(o => o.id === pl);
                      return po ? <span key={pl} className="text-[11px] text-text-muted">{po.flag} {po.label}</span> : null;
                    })}
                    <span className="text-[10px] text-text-muted">·</span>
                    <span className="text-[10px] text-text-muted">{new Date(p.created_at).toLocaleString('es')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[p.status] || 'bg-white/5 text-text-muted'}`}>
                    {p.status || 'pendiente'}
                  </span>
                  {p.video_url && (
                    <a href={p.video_url} target="_blank" rel="noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
                      <ExternalLink className="w-3 h-3" /> Ver
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Panel principal ─────────────────────────────────────────────────────── */
export default function SimpleConnectionsHub({ authFetch }) {
  const [activeTab, setActiveTab] = useState('canales');

  const TABS = [
    { id: 'canales', label: 'Conexiones' },
    { id: 'publicar', label: '▶ Publicar' },
    { id: 'wizard', label: 'Onboarding Clientes' },
  ];

  return (
    <div className="space-y-6" data-testid="simple-connections-hub">
      {/* Header */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-heading font-bold text-text-heading flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary" />
          </span>
          Conexiones · Hub de Publicación
        </h2>
        <p className="text-text-muted text-sm mt-2">
          Aynara orquesta toda la publicidad de tus clientes desde aquí. Conecta una vez, publica en todas las redes.
        </p>

        {/* How it works */}
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          {[
            { icon: '1', text: 'Conecta Publer con tu API Key (30 segundos)', color: 'bg-primary/10 text-primary border-primary/20' },
            { icon: '2', text: 'Dentro de Publer conecta las redes sociales de tus clientes', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
            { icon: '3', text: 'Publica videos e imágenes en Instagram, TikTok, Facebook y LinkedIn desde aquí', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
          ].map(s => (
            <div key={s.icon} className={`flex items-start gap-3 p-3 rounded-xl border ${s.color}`}>
              <span className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${s.color} border`}>{s.icon}</span>
              <p className="text-xs leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`connections-tab-${t.id}`}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === t.id ? 'bg-primary text-[#0A1A3F]' : 'bg-card border border-border-subtle text-text-muted hover:border-primary/40'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'canales' && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-text-muted uppercase tracking-wider px-1">Canales activos</h3>
          <PublerCard authFetch={authFetch} />
          <WhatsAppCard authFetch={authFetch} />
          <EmailCard />
          <AgencyAccessGuide />
        </div>
      )}

      {activeTab === 'publicar' && (
        <PublerPublishPanel authFetch={authFetch} />
      )}

      {activeTab === 'wizard' && (
        <PublerClientWizard authFetch={authFetch} />
      )}
    </div>
  );
}
